import React from "react";
import { useTranslation } from "react-i18next";

export interface HailingHeaderProps {
  activeTab: "ride" | "trips";
  menuOpen: boolean;
  /** The logo tile: opens the nav menu on mobile; does nothing on desktop. */
  onMenuTrigger: () => void;
  onSelectRide: () => void;
  onSelectMyTrips: () => void;
  onOpenSupport: () => void;
  signedIn: boolean;
  onSignIn: () => void;
}

// The web hailing header: logo and mobile menu, Ride / My trips tabs, language,
// Support and the account control.
const HailingHeader: React.FC<HailingHeaderProps> = ({
  activeTab,
  menuOpen,
  onMenuTrigger,
  onSelectRide,
  onSelectMyTrips,
  onOpenSupport,
  signedIn,
  onSignIn,
}) => {
  const { t, i18n } = useTranslation("hailing");
  const isZh = i18n.language.startsWith("zh");
  // Per Roy's mockup (Web Hailing.dc.html, "Header"): 64px white bar, 28px gaps,
  // 24px side padding, soft drop shadow. Mobile: tabs + Support hidden below md,
  // wordmark below sm; the language toggle and Sign in/avatar are always visible.
  return (
    <header className="sticky top-0 z-20 flex h-16 shrink-0 items-center gap-4 bg-white px-4 shadow-[0_4px_16px_rgba(15,21,26,0.08)] md:gap-7 md:px-6">
      {/* Logo: 34px rounded tile + wordmark. Below md the tile doubles as
              the nav menu trigger (dropdown with Ride / My trips / Support). */}
      <div className="flex shrink-0 items-center gap-2.5">
        <button
          type="button"
          onClick={onMenuTrigger}
          aria-label={t("header.openMenu")}
          aria-expanded={menuOpen}
          data-nav-trigger
          className="relative flex h-[34px] w-[34px] items-center justify-center rounded-[10px] bg-brand-blue md:cursor-default"
        >
          <span className="font-material-symbols text-[22px] leading-none text-white [font-variation-settings:'FILL'_1]">
            local_taxi
          </span>
        </button>
        <span className="hidden text-[20px] font-bold tracking-[-0.4px] text-ink-100 sm:block">
          GetThere
        </span>
      </div>

      {/* Mobile nav dropdown — anchored under the logo tile */}
      {menuOpen && (
        <nav
          data-nav-menu
          className="absolute left-4 top-[52px] z-30 w-56 overflow-hidden rounded-2xl bg-white py-2 shadow-[0_16px_48px_rgba(15,21,26,0.22)] md:hidden"
        >
          <button
            type="button"
            onClick={onSelectRide}
            className="flex w-full items-center gap-3 px-4 py-3 text-left text-[14px] font-medium text-ink-100 active:bg-[#F3F5F7]"
          >
            <span className="font-material-symbols text-[20px] leading-none text-brand-blue">
              local_taxi
            </span>
            {t("header.ride")}
          </button>
          <button
            type="button"
            onClick={onSelectMyTrips}
            className="flex w-full items-center gap-3 px-4 py-3 text-left text-[14px] font-medium text-ink-100 active:bg-[#F3F5F7]"
          >
            <span className="font-material-symbols text-[20px] leading-none text-brand-blue">
              route
            </span>
            {t("header.myTrips")}
          </button>
          <button
            type="button"
            onClick={onOpenSupport}
            className="flex w-full items-center gap-3 px-4 py-3 text-left text-[14px] font-medium text-ink-100 active:bg-[#F3F5F7]"
          >
            <span className="font-material-symbols text-[20px] leading-none text-brand-blue">
              support_agent
            </span>
            {t("header.support")}
          </button>
        </nav>
      )}

      {/* Tabs (md+): Ride active with blue underline flush to header bottom */}
      <nav className="hidden h-full items-center gap-[22px] text-[14px] font-medium md:flex">
        <button
          type="button"
          onClick={onSelectRide}
          className={`flex h-full items-center border-b-2 ${
            activeTab === "ride"
              ? "border-brand-blue text-ink-100"
              : "border-transparent text-[#6B7075]"
          }`}
        >
          {t("header.ride")}
        </button>
        <button
          type="button"
          onClick={onSelectMyTrips}
          className={`flex h-full items-center border-b-2 ${
            activeTab === "trips"
              ? "border-brand-blue text-ink-100"
              : "border-transparent text-[#6B7075]"
          }`}
        >
          {t("header.myTrips")}
        </button>
      </nav>

      <div className="flex-1" />

      {/* Language (all breakpoints): globe icon + other-language label */}
      <button
        type="button"
        onClick={() => i18n.changeLanguage(isZh ? "en" : "zh")}
        aria-label={t("header.languageToggle")}
        className="flex shrink-0 items-center gap-1.5 whitespace-nowrap text-[14px] text-[#3A4045]"
      >
        <span className="font-material-symbols text-[20px] leading-none">
          language
        </span>
        {isZh ? "English" : "繁體中文"}
      </button>

      {/* Support (md+ only — low-priority link): opens the DASH Taxi contact
              pop-up. Below md it lives in the logo-tile nav menu. */}
      <button
        type="button"
        onClick={onOpenSupport}
        className="hidden shrink-0 whitespace-nowrap text-[14px] text-[#3A4045] md:block"
      >
        {t("header.support")}
      </button>

      {signedIn ? (
        <div
          role="img"
          aria-label={t("header.account")}
          className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full bg-[#DCEBF7] text-brand-blue"
        >
          <span
            aria-hidden="true"
            className="font-material-symbols text-[21px] leading-none"
          >
            person
          </span>
        </div>
      ) : (
        <button
          type="button"
          onClick={onSignIn}
          className="h-[38px] shrink-0 whitespace-nowrap rounded-[19px] border-[1.5px] border-brand-blue bg-white px-4 text-[14px] font-semibold text-brand-blue md:px-[18px]"
        >
          {t("header.signIn")}
        </button>
      )}
    </header>
  );
};

export default HailingHeader;
